import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign, Users, Store, Crown, QrCode, AlertTriangle, TrendingUp, Download,
  CheckCircle, XCircle, Star, Megaphone, Plus, Trophy,
} from "lucide-react";

const BRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

const EMPTY_ANILHA = { name: "", tier: "2d", tagline: "", price: 0, cost: 0, sizes: "", colors: "", stock: 0, available: true, production_days: 7, description: "", photos: "", features: "" };

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl p-5 border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="w-4.5 h-4.5 w-5 h-5" />
      </div>
      <p className="font-heading font-bold text-2xl text-brand-graphite">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [orders, setOrders] = useState([]);
  const [provFilter, setProvFilter] = useState("all");
  const [sellerForm, setSellerForm] = useState({ name: "", email: "", pix_key: "" });
  const [partnerForm, setPartnerForm] = useState({ name: "", banner_url: "", link: "", geo_radius: "cidade", city: "Joinville" });
  const [broadcast, setBroadcast] = useState({ title: "", message: "", audience: "all" });
  const [sellerDialog, setSellerDialog] = useState(false);
  const [partnerDialog, setPartnerDialog] = useState(false);
  const [tagsList, setTagsList] = useState([]);
  const [collars, setCollars] = useState([]);
  const [collarForm, setCollarForm] = useState({ name: "", partner_name: "", image_url: "", price: 0, link: "", material: "corrente" });
  const [collarDialog, setCollarDialog] = useState(false);
  const [collarOrders, setCollarOrders] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [interestLeads, setInterestLeads] = useState([]);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [anilhas, setAnilhas] = useState([]);
  const [anilhaOrders, setAnilhaOrders] = useState([]);
  const [anilhaForm, setAnilhaForm] = useState(EMPTY_ANILHA);
  const [anilhaDialog, setAnilhaDialog] = useState(false);
  const [editingAnilha, setEditingAnilha] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data));
    api.get(`/admin/providers${provFilter !== "all" ? `?status=${provFilter}` : ""}`).then((r) => setProviders(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/partners").then((r) => setPartners(r.data));
    api.get("/admin/sellers").then((r) => setSellers(r.data));
    api.get("/admin/commissions").then((r) => setCommissions(r.data));
    api.get("/admin/sellers/ranking").then((r) => setRanking(r.data));
    api.get("/admin/reviews").then((r) => setReviews(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/tags").then((r) => setTagsList(r.data));
    api.get("/admin/collars").then((r) => setCollars(r.data));
    api.get("/admin/collar-orders").then((r) => setCollarOrders(r.data));
    api.get("/admin/feedback").then((r) => setFeedbackList(r.data));
    api.get("/admin/interest-leads").then((r) => setInterestLeads(r.data));
    api.get("/admin/deletion-requests").then((r) => setDeletionRequests(r.data));
    api.get("/admin/anilhas").then((r) => setAnilhas(r.data));
    api.get("/admin/anilha-orders").then((r) => setAnilhaOrders(r.data));
  }, [provFilter]);

  useEffect(() => { load(); }, [load]);

  const cleanupDemo = async () => {
    if (!window.confirm("Isso remove DEFINITIVAMENTE todos os dados de demonstração/teste: prestadores fictícios, contas demo, pedidos de teste e pendentes. Permanecem: clientes reais, testadores convidados e vendas reais pagas. Continuar?")) return;
    try {
      const r = await api.post("/admin/cleanup-demo?confirm=APAGAR-DEMO");
      const total = Object.values(r.data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Limpeza concluída: ${total} registros de demonstração removidos`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na limpeza");
    }
  };

  const processDeletion = async (rid) => {
    if (!window.confirm("Excluir DEFINITIVAMENTE a conta e os dados pessoais deste usuário? Pedidos pagos são mantidos apenas como registro fiscal, sem vínculo pessoal.")) return;
    try {
      const r = await api.post(`/admin/deletion-requests/${rid}/process`);
      toast.success(r.data.user_found ? "Conta e dados pessoais excluídos" : "Solicitação processada (conta já não existia)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao processar");
    }
  };

  const provAction = async (pid, action, seller_id) => {
    await api.put(`/admin/providers/${pid}`, { action, seller_id });
    toast.success("Atualizado");
    load();
  };

  const userAction = async (uid, action) => {
    await api.put(`/admin/users/${uid}`, { action });
    toast.success("Usuário atualizado");
    load();
  };

  const createSeller = async () => {
    if (!sellerForm.name || !sellerForm.pix_key) return toast.error("Preencha nome e chave Pix");
    await api.post("/admin/sellers", sellerForm);
    toast.success("Vendedor cadastrado");
    setSellerForm({ name: "", email: "", pix_key: "" });
    setSellerDialog(false);
    load();
  };

  const createPartner = async () => {
    if (!partnerForm.name) return toast.error("Informe o nome do parceiro");
    await api.post("/admin/partners", { ...partnerForm, active: true });
    toast.success("Parceiro cadastrado");
    setPartnerForm({ name: "", banner_url: "", link: "", geo_radius: "cidade", city: "Joinville" });
    setPartnerDialog(false);
    load();
  };

  const createCollar = async () => {
    if (!collarForm.name) return toast.error("Informe o modelo da coleira");
    await api.post("/admin/collars", { ...collarForm, active: true });
    toast.success("Coleira cadastrada");
    setCollarForm({ name: "", partner_name: "", image_url: "", price: 0, link: "", material: "corrente" });
    setCollarDialog(false);
    load();
  };

  const saveAnilha = async () => {
    if (!anilhaForm.name) return toast.error("Informe o nome do modelo");
    const csv = (s) => String(s).split(",").map((x) => x.trim()).filter(Boolean);
    const payload = {
      ...anilhaForm,
      price: Number(anilhaForm.price) || 0, cost: Number(anilhaForm.cost) || 0,
      stock: Number(anilhaForm.stock) || 0, production_days: Number(anilhaForm.production_days) || 0,
      sizes: csv(anilhaForm.sizes), colors: csv(anilhaForm.colors),
      photos: csv(anilhaForm.photos), features: csv(anilhaForm.features),
    };
    if (editingAnilha) await api.put(`/admin/anilhas/${editingAnilha}`, payload);
    else await api.post("/admin/anilhas", payload);
    toast.success("Modelo salvo");
    setAnilhaDialog(false);
    setEditingAnilha(null);
    setAnilhaForm(EMPTY_ANILHA);
    load();
  };

  const openEditAnilha = (p) => {
    setEditingAnilha(p.product_id);
    setAnilhaForm({
      ...p, sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "),
      photos: (p.photos || []).join(", "), features: (p.features || []).join(", "),
    });
    setAnilhaDialog(true);
  };

  const sendBroadcast = async () => {
    if (!broadcast.title || !broadcast.message) return toast.error("Preencha título e mensagem");
    await api.post("/admin/notifications/broadcast", broadcast);
    toast.success("Notificação registrada para envio");
    setBroadcast({ title: "", message: "", audience: "all" });
  };

  const exportCsv = (path) => {
    const t = localStorage.getItem("pmh_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = path.split("/").slice(-2, -1)[0] + ".csv";
        a.click();
      });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold font-heading text-brand-graphite mb-1">Painel Administrativo</h1>
      <p className="text-muted-foreground mb-8">PetMachine Hub — gestão completa do ecossistema</p>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-white border flex-wrap h-auto" data-testid="admin-tabs">
          <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="providers" data-testid="admin-tab-providers">Prestadores</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Tutores</TabsTrigger>
          <TabsTrigger value="sellers" data-testid="admin-tab-sellers">Vendedores</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners">Parceiros</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avaliações</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Pedidos</TabsTrigger>
          <TabsTrigger value="tags" data-testid="admin-tab-tags">Tags</TabsTrigger>
          <TabsTrigger value="coleiras" data-testid="admin-tab-collars">Coleiras</TabsTrigger>
          <TabsTrigger value="anilhas" data-testid="admin-tab-anilhas">Anilhas</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast">Notificações</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          {metrics && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-grid">
                <StatCard icon={DollarSign} label="MRR total" value={BRL(metrics.mrr)} accent="bg-brand-mintLight text-brand-mintDark" />
                <StatCard icon={TrendingUp} label="Churn" value={`${metrics.churn_rate}%`} accent="bg-red-50 text-red-600" />
                <StatCard icon={Crown} label="Assinantes Club" value={`${metrics.club_subscribers} (${metrics.early_bird_remaining} early birds restantes)`} accent="bg-brand-mustardLight text-brand-mustardDark" />
                <StatCard icon={QrCode} label="Tags vendidas" value={`${metrics.tag_sales} (${BRL(metrics.tag_revenue)})`} accent="bg-brand-coralLight text-brand-coral" />
                <StatCard icon={Store} label="Prestadores ativos" value={metrics.providers_approved} accent="bg-brand-mintLight text-brand-mintDark" />
                <StatCard icon={AlertTriangle} label="Aguardando aprovação" value={metrics.providers_pending} accent="bg-brand-mustardLight text-brand-mustardDark" />
                <StatCard icon={Users} label="Tutores cadastrados" value={metrics.tutors} accent="bg-brand-coralLight text-brand-coral" />
                <StatCard icon={AlertTriangle} label="Pets desaparecidos" value={metrics.lost_pets_active} accent="bg-red-50 text-red-600" />
              </div>
              <div className="bg-white rounded-2xl p-6 border mt-6" data-testid="revenue-by-source">
                <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-4">Receita por fonte</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-brand-mintLight rounded-xl p-4"><p className="text-xs text-brand-mintDark font-semibold">Assinaturas Club (MRR)</p><p className="font-heading font-bold text-xl">{BRL(metrics.club_mrr)}</p></div>
                  <div className="bg-brand-coralLight rounded-xl p-4"><p className="text-xs text-brand-coral font-semibold">Planos Prestadores (MRR)</p><p className="font-heading font-bold text-xl">{BRL(metrics.plan_mrr)}</p></div>
                  <div className="bg-brand-mustardLight rounded-xl p-4"><p className="text-xs text-brand-mustardDark font-semibold">Tags</p><p className="font-heading font-bold text-xl">{BRL(metrics.tag_revenue)}</p></div>
                  <div className="bg-brand-cream rounded-xl p-4 border"><p className="text-xs text-muted-foreground font-semibold">Banhos avulsos</p><p className="font-heading font-bold text-xl">{BRL(metrics.revenue_by_source?.bath)}</p></div>
                </div>
              </div>

              {deletionRequests.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border mt-6" data-testid="deletion-requests-section">
                  <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-4">Pedidos de exclusão de conta — LGPD ({deletionRequests.filter((r) => r.status === "pendente").length} pendentes)</h3>
                  <div className="space-y-2">
                    {deletionRequests.map((r) => (
                      <div key={r.request_id} className="bg-brand-cream rounded-xl px-4 py-3 flex items-center justify-between gap-4 text-sm" data-testid={`deletion-request-${r.request_id}`}>
                        <div>
                          <p className="font-semibold text-brand-graphite">{r.name} <span className="font-normal text-muted-foreground">({r.email})</span></p>
                          <p className="text-xs text-muted-foreground">{r.reason || "sem motivo informado"} • {r.created_at?.slice(0, 10).split("-").reverse().join("/")}</p>
                        </div>
                        {r.status === "pendente" ? (
                          <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50 shrink-0" onClick={() => processDeletion(r.request_id)} data-testid={`process-deletion-${r.request_id}`}>Excluir dados</Button>
                        ) : (
                          <span className="text-xs font-semibold text-brand-mintDark shrink-0">Processado</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {interestLeads.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border mt-6" data-testid="interest-leads-section">
                  <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-4">Interessados no Banho Móvel ({interestLeads.length})</h3>
                  <div className="space-y-2">
                    {interestLeads.map((l) => (
                      <div key={l.lead_id} className="bg-brand-cream rounded-xl px-4 py-3 flex items-center justify-between gap-4 text-sm">
                        <div><p className="font-semibold text-brand-graphite">{l.name}</p><p className="text-xs text-muted-foreground">{l.phone}{l.neighborhood ? ` • ${l.neighborhood}` : ""}</p></div>
                        <span className="text-xs text-muted-foreground">{l.created_at?.slice(0, 10).split("-").reverse().join("/")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl p-6 border mt-6" data-testid="feedback-section">
                <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-4">Feedback dos testadores ({feedbackList.length})</h3>
                {feedbackList.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum feedback ainda. Compartilhe a página /testadores com seus testadores.</p>
                ) : (
                  <div className="space-y-3">
                    {feedbackList.map((f) => (
                      <div key={f.feedback_id} className="bg-brand-cream rounded-xl px-4 py-3 flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-brand-graphite">{f.name || "Anônimo"} <span className="text-xs font-normal text-muted-foreground">• {f.created_at?.slice(0, 10).split("-").reverse().join("/")}</span></p>
                          <p className="text-sm text-muted-foreground mt-0.5">{f.message}</p>
                        </div>
                        <span className="flex items-center gap-0.5 shrink-0">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < f.rating ? "fill-brand-mustard text-brand-mustard" : "text-gray-200"}`} />
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="providers" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <Select value={provFilter} onValueChange={setProvFilter}>
              <SelectTrigger className="w-52 bg-white" data-testid="admin-provider-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="approved">Aprovados</SelectItem>
                <SelectItem value="suspended">Suspensos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-providers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Negócio</TableHead><TableHead>Categoria</TableHead><TableHead>Plano</TableHead>
                  <TableHead>Status</TableHead><TableHead>Vendedor</TableHead><TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((p) => (
                  <TableRow key={p.provider_id} data-testid={`admin-provider-${p.provider_id}`}>
                    <TableCell className="font-medium">{p.business_name}{p.verified && " ✓"}</TableCell>
                    <TableCell className="text-xs">{p.category}</TableCell>
                    <TableCell className="text-xs">{p.plan}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.status === "approved" ? "bg-brand-mintLight text-brand-mintDark" : p.status === "pending" ? "bg-brand-mustardLight text-brand-mustardDark" : "bg-red-100 text-red-700"}`}>
                        {p.status === "approved" ? "Aprovado" : p.status === "pending" ? "Pendente" : "Suspenso"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select value={p.seller_id || "none"} onValueChange={(v) => provAction(p.provider_id, "assign_seller", v === "none" ? null : v)}>
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`assign-seller-${p.provider_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem vendedor</SelectItem>
                          {sellers.map((s) => <SelectItem key={s.seller_id} value={s.seller_id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {p.status !== "approved" && <Button size="sm" variant="ghost" onClick={() => provAction(p.provider_id, "approve")} data-testid={`approve-${p.provider_id}`}><CheckCircle className="w-4 h-4 text-brand-mint" /></Button>}
                      {p.status === "approved" && <Button size="sm" variant="ghost" onClick={() => provAction(p.provider_id, "suspend")} data-testid={`suspend-${p.provider_id}`}><XCircle className="w-4 h-4 text-red-500" /></Button>}
                      {p.status === "suspended" && <Button size="sm" variant="ghost" onClick={() => provAction(p.provider_id, "reactivate")}><CheckCircle className="w-4 h-4 text-brand-mint" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => provAction(p.provider_id, p.verified ? "unverify" : "verify")} title="Selo verificado" data-testid={`verify-${p.provider_id}`}>
                        <CheckCircle className={`w-4 h-4 ${p.verified ? "text-brand-mint" : "text-gray-300"}`} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => provAction(p.provider_id, p.featured ? "unfeature" : "feature")} title="Destaque" data-testid={`feature-${p.provider_id}`}>
                        <Star className={`w-4 h-4 ${p.featured ? "fill-brand-mustard text-brand-mustard" : "text-gray-300"}`} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-users-table">
            <Table>
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Papel</TableHead><TableHead>Club</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs">{u.email}</TableCell>
                    <TableCell className="text-xs">{u.role}</TableCell>
                    <TableCell>{u.club?.active ? <Crown className="w-4 h-4 text-brand-mustard" /> : "—"}</TableCell>
                    <TableCell className="text-xs">{u.status === "active" ? "Ativo" : "Suspenso"}</TableCell>
                    <TableCell className="text-right">
                      {u.role !== "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => userAction(u.user_id, u.status === "active" ? "suspend" : "activate")} data-testid={`user-toggle-${u.user_id}`}>
                          {u.status === "active" ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-brand-mint" />}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="sellers" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-lg">Vendedores</h3>
                  <Dialog open={sellerDialog} onOpenChange={setSellerDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="add-seller-btn"><Plus className="w-4 h-4 mr-1" /> Novo vendedor</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Cadastrar vendedor</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div><Label>Nome</Label><Input value={sellerForm.name} onChange={(e) => setSellerForm({ ...sellerForm, name: e.target.value })} className="mt-1" data-testid="seller-name-input" /></div>
                        <div><Label>Email</Label><Input value={sellerForm.email} onChange={(e) => setSellerForm({ ...sellerForm, email: e.target.value })} className="mt-1" data-testid="seller-email-input" /></div>
                        <div><Label>Chave Pix</Label><Input value={sellerForm.pix_key} onChange={(e) => setSellerForm({ ...sellerForm, pix_key: e.target.value })} className="mt-1" data-testid="seller-pix-input" /></div>
                        <Button onClick={createSeller} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="seller-save-btn">Cadastrar</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Chave Pix</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {sellers.map((s) => (
                      <TableRow key={s.seller_id}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="text-xs font-mono">{s.pix_key}</TableCell>
                        <TableCell className="text-xs">{s.active ? "Ativo" : "Inativo"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="bg-white rounded-2xl border p-6" data-testid="commissions-table">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-lg">Comissões (100% do 1º mês + 15% recorrente)</h3>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv("/admin/commissions/export")} data-testid="export-commissions-btn">
                    <Download className="w-4 h-4 mr-1" /> Exportar CSV
                  </Button>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Vendedor</TableHead><TableHead>Prestador</TableHead><TableHead>Tipo</TableHead><TableHead>Mês</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {commissions.map((c) => (
                      <TableRow key={c.commission_id}>
                        <TableCell className="font-medium text-sm">{c.seller_name}</TableCell>
                        <TableCell className="text-xs">{c.provider_name}</TableCell>
                        <TableCell className="text-xs">{c.type === "first_month" ? "1º mês (100%)" : "Recorrente (15%)"}</TableCell>
                        <TableCell className="text-xs">{c.month}</TableCell>
                        <TableCell className="font-semibold text-sm">{BRL(c.amount)}</TableCell>
                        <TableCell><span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.paid ? "bg-brand-mintLight text-brand-mintDark" : "bg-brand-mustardLight text-brand-mustardDark"}`}>{c.paid ? "Pago" : "Pendente"}</span></TableCell>
                        <TableCell>{!c.paid && <Button size="sm" variant="ghost" onClick={async () => { await api.put(`/admin/commissions/${c.commission_id}/paid`); toast.success("Marcada como paga"); load(); }} data-testid={`pay-commission-${c.commission_id}`}><CheckCircle className="w-4 h-4 text-brand-mint" /></Button>}</TableCell>
                      </TableRow>
                    ))}
                    {commissions.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Nenhuma comissão ainda.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="bg-white rounded-2xl border p-6 h-fit" data-testid="sellers-ranking">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-brand-mustard" /> Ranking de vendas</h3>
              <div className="space-y-3">
                {ranking.map((r, i) => (
                  <div key={r.seller_id} className="flex items-center gap-3 bg-brand-cream rounded-xl p-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center font-heading font-bold text-sm ${i === 0 ? "bg-brand-mustard text-brand-graphite" : "bg-white text-muted-foreground border"}`}>{i + 1}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{r.seller_name}</p>
                      <p className="text-xs text-muted-foreground">{r.providers_linked} prestador(es) • {r.commissions_count} comissões</p>
                    </div>
                    <p className="font-heading font-bold text-brand-mintDark">{BRL(r.total_commission)}</p>
                  </div>
                ))}
                {ranking.length === 0 && <p className="text-sm text-muted-foreground">Sem dados ainda.</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={partnerDialog} onOpenChange={setPartnerDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="add-partner-btn"><Plus className="w-4 h-4 mr-1" /> Novo parceiro</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Cadastrar parceiro</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome</Label><Input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} className="mt-1" data-testid="partner-name-input" /></div>
                  <div><Label>URL do banner</Label><Input value={partnerForm.banner_url} onChange={(e) => setPartnerForm({ ...partnerForm, banner_url: e.target.value })} className="mt-1" data-testid="partner-banner-input" /></div>
                  <div><Label>Link de destino</Label><Input value={partnerForm.link} onChange={(e) => setPartnerForm({ ...partnerForm, link: e.target.value })} className="mt-1" data-testid="partner-link-input" /></div>
                  <div>
                    <Label>Raio de exibição</Label>
                    <Select value={partnerForm.geo_radius} onValueChange={(v) => setPartnerForm({ ...partnerForm, geo_radius: v })}>
                      <SelectTrigger className="mt-1" data-testid="partner-radius-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["5km", "10km", "cidade", "estado"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={createPartner} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="partner-save-btn">Cadastrar</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-partners-grid">
            {partners.map((p) => (
              <div key={p.partner_id} className="bg-white rounded-2xl border overflow-hidden">
                {p.banner_url && <img src={p.banner_url} alt={p.name} className="w-full h-32 object-cover" />}
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-heading font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">Raio: {p.geo_radius} • {p.city}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/partners/${p.partner_id}`); toast.success("Removido"); load(); }} data-testid={`delete-partner-${p.partner_id}`}>
                    <XCircle className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <div className="bg-white rounded-2xl border" data-testid="admin-reviews-table">
            <Table>
              <TableHeader><TableRow><TableHead>Prestador</TableHead><TableHead>Tutor</TableHead><TableHead>Nota</TableHead><TableHead>Comentário</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ação</TableHead></TableRow></TableHeader>
              <TableBody>
                {reviews.map((r) => (
                  <TableRow key={r.review_id}>
                    <TableCell className="text-xs">{r.provider_id}</TableCell>
                    <TableCell className="text-sm">{r.tutor_name}</TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm"><Star className="w-3.5 h-3.5 fill-brand-mustard text-brand-mustard" />{r.rating}</span></TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate">{r.comment}</TableCell>
                    <TableCell className="text-xs">{r.status === "visible" ? "Visível" : "Oculta"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={async () => { await api.put(`/admin/reviews/${r.review_id}`, { action: r.status === "visible" ? "hide" : "show" }); toast.success("Atualizado"); load(); }} data-testid={`moderate-${r.review_id}`}>
                        {r.status === "visible" ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-brand-mint" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="flex justify-between mb-4">
            <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={cleanupDemo} data-testid="cleanup-demo-btn">
              <AlertTriangle className="w-4 h-4 mr-1" /> Limpar dados de demonstração
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv("/admin/orders/export")} data-testid="export-orders-btn">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-orders-table">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Comprador</TableHead><TableHead>Endereço de entrega</TableHead><TableHead>Valor</TableHead><TableHead>Pagamento</TableHead><TableHead>Status do produto</TableHead><TableHead>Data</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.map((o) => {
                  const a = o.delivery_address;
                  const addr = a ? `${a.rua || ""}, ${a.numero || ""}${a.complemento ? " - " + a.complemento : ""} — ${a.bairro || ""}, ${a.cidade || "Joinville"} — CEP ${a.cep || ""}` : null;
                  const physical = ["tag", "collar", "anilha"].includes(o.kind);
                  return (
                    <TableRow key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
                      <TableCell className="text-sm"><p className="font-medium">{o.item}</p><p className="text-xs text-muted-foreground font-mono">{o.order_id}</p></TableCell>
                      <TableCell className="text-xs"><p className="font-medium text-sm">{o.buyer_name || "—"}</p><p>{o.buyer_email}</p><p>{o.buyer_phone}</p></TableCell>
                      <TableCell className="text-xs max-w-[220px]">{addr || "—"}</TableCell>
                      <TableCell className="font-semibold">{BRL(o.amount)}</TableCell>
                      <TableCell className="text-xs">{o.status}{o.mp_status ? ` (${o.mp_status})` : ""}</TableCell>
                      <TableCell>
                        {physical && o.status === "approved" ? (
                          <Select value={o.fulfillment_status || "aprovado"} onValueChange={async (v) => { await api.put(`/admin/orders/${o.order_id}/fulfillment`, { status: v }); toast.success("Status atualizado e cliente avisado"); load(); }}>
                            <SelectTrigger className="w-40 h-8 text-xs" data-testid={`order-status-select-${o.order_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="aprovado">Compra aprovada</SelectItem>
                              <SelectItem value="preparando">Em preparação</SelectItem>
                              <SelectItem value="enviado">Produto enviado</SelectItem>
                              <SelectItem value="entregue">Entregue</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : <span className="text-xs text-muted-foreground">{physical ? "Aguardando pagamento" : "—"}</span>}
                      </TableCell>
                      <TableCell className="text-xs">{o.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  );
                })}
                {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Nenhum pedido ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="tags" className="mt-6">
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-tags-table">
            <Table>
              <TableHeader><TableRow><TableHead>QR</TableHead><TableHead>Tag / Modelo</TableHead><TableHead>Comprador</TableHead><TableHead>Gravação (frente)</TableHead><TableHead>Endereço de entrega</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Exportar QR</TableHead></TableRow></TableHeader>
              <TableBody>
                {tagsList.map((t) => {
                  const a = t.delivery_address;
                  const addr = a ? `${a.rua || ""}, ${a.numero || ""}${a.complemento ? " - " + a.complemento : ""} — ${a.bairro || ""}, ${a.cidade || "Joinville"} — CEP ${a.cep || ""}` : "Não informado";
                  return (
                    <TableRow key={t.tag_code} data-testid={`admin-tag-${t.tag_code}`}>
                      <TableCell><img src={`${process.env.REACT_APP_BACKEND_URL}/api/tags/${t.tag_code}/qr.png`} alt="QR" className="w-12 h-12 rounded border" /></TableCell>
                      <TableCell><p className="font-mono text-xs font-bold text-brand-coral">{t.tag_code}</p><p className="text-xs text-muted-foreground">{t.model || t.type}</p></TableCell>
                      <TableCell className="text-xs"><p className="font-medium text-sm">{t.buyer_name}</p><p>{t.buyer_email}</p><p>{t.buyer_phone}</p></TableCell>
                      <TableCell className="text-xs">{t.engraving ? (<><p className="font-medium">{t.engraving.pet_name}</p><p className="text-muted-foreground">{t.engraving.phone}</p></>) : (t.pet_name || "—")}</TableCell>
                      <TableCell className="text-xs max-w-[220px]">{addr}</TableCell>
                      <TableCell className="text-sm font-semibold">{t.amount != null ? BRL(t.amount) : "—"}</TableCell>
                      <TableCell>
                        <Select value={t.status} onValueChange={async (v) => { await api.put(`/admin/tags/${t.tag_code}/status`, { status: v }); toast.success("Status atualizado e comprador avisado"); load(); }}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`tag-status-select-${t.tag_code}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="confeccao">Em confecção</SelectItem>
                            <SelectItem value="enviado">Enviado</SelectItem>
                            <SelectItem value="entregue">Entregue</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tags/${t.tag_code}/qr.svg`} download data-testid={`export-svg-${t.tag_code}`}><Button size="sm" variant="outline" className="h-7 text-xs rounded-full mr-1"><Download className="w-3 h-3 mr-1" />SVG (laser)</Button></a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tags/${t.tag_code}/qr.png`} download data-testid={`export-png-${t.tag_code}`}><Button size="sm" variant="outline" className="h-7 text-xs rounded-full"><Download className="w-3 h-3 mr-1" />PNG</Button></a>
                        {t.engraving && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tags/${t.tag_code}/frente.svg`} download data-testid={`export-frente-${t.tag_code}`}><Button size="sm" variant="outline" className="h-7 text-xs rounded-full ml-1"><Download className="w-3 h-3 mr-1" />Frente</Button></a>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {tagsList.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Nenhuma tag vendida ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Para gravação em inox na fiber laser (Translaser Mini Fiber 50W / EZCAD), use o arquivo <strong>SVG</strong> (vetorial, importa direto no EZCAD). O PNG atende impressoras comuns para adesivos e cartazes.</p>
        </TabsContent>

        <TabsContent value="coleiras" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={collarDialog} onOpenChange={setCollarDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="add-collar-btn"><Plus className="w-4 h-4 mr-1" /> Nova coleira</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Cadastrar coleira</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Modelo</Label><Input value={collarForm.name} onChange={(e) => setCollarForm({ ...collarForm, name: e.target.value })} className="mt-1" data-testid="collar-name-input" /></div>
                  <div><Label>Parceiro</Label><Input value={collarForm.partner_name} onChange={(e) => setCollarForm({ ...collarForm, partner_name: e.target.value })} className="mt-1" data-testid="collar-partner-input" /></div>
                  <div><Label>URL da foto</Label><Input value={collarForm.image_url} onChange={(e) => setCollarForm({ ...collarForm, image_url: e.target.value })} className="mt-1" data-testid="collar-image-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Preço (R$)</Label><Input type="number" value={collarForm.price} onChange={(e) => setCollarForm({ ...collarForm, price: Number(e.target.value) })} className="mt-1" data-testid="collar-price-input" /></div>
                    <div>
                      <Label>Material</Label>
                      <Select value={collarForm.material} onValueChange={(v) => setCollarForm({ ...collarForm, material: v })}>
                        <SelectTrigger className="mt-1" data-testid="collar-material-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="couro">Couro (com gravação)</SelectItem>
                          <SelectItem value="corrente">Corrente (pronta entrega)</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Link de compra (parceiro)</Label><Input value={collarForm.link} onChange={(e) => setCollarForm({ ...collarForm, link: e.target.value })} className="mt-1" data-testid="collar-link-input" /></div>
                  <Button onClick={createCollar} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="collar-save-btn">Cadastrar</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-collars-grid">
            {collars.map((c) => (
              <div key={c.collar_id} className="bg-white rounded-2xl border overflow-hidden">
                {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-32 object-cover" />}
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-heading font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.partner_name} • {BRL(c.price)} • {c.material === "couro" ? "Couro c/ gravação" : "Pronta entrega"}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/collars/${c.collar_id}`); toast.success("Removida"); load(); }} data-testid={`delete-collar-${c.collar_id}`}>
                    <XCircle className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <h3 className="font-heading font-semibold text-lg mt-10 mb-4">Pedidos de coleiras</h3>
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-collar-orders-table">
            <Table>
              <TableHeader><TableRow><TableHead>Produto</TableHead><TableHead>Comprador</TableHead><TableHead>Personalização</TableHead><TableHead>Endereço</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {collarOrders.map((o) => {
                  const a = o.delivery_address;
                  const addr = a ? `${a.rua || ""}, ${a.numero || ""}${a.complemento ? " - " + a.complemento : ""} — ${a.bairro || ""}, ${a.cidade || "Joinville"} — CEP ${a.cep || ""}` : "Não informado";
                  return (
                    <TableRow key={o.collar_order_id} data-testid={`collar-order-${o.collar_order_id}`}>
                      <TableCell className="text-sm font-medium">{o.collar_name}<p className="text-xs text-muted-foreground">{o.material === "couro" ? "Couro c/ gravação" : "Corrente"}</p></TableCell>
                      <TableCell className="text-xs"><p className="font-medium text-sm">{o.buyer_name}</p><p>{o.buyer_email}</p></TableCell>
                      <TableCell className="text-xs max-w-[240px]">
                        {o.engraving ? (<><p>Gravar: <strong>{o.engraving.pet_name}</strong> • {o.engraving.phone}</p><p className="font-mono">QR: {o.engraving.qr_code}</p></>) : "—"}
                        {o.neck_cm && <p>Pescoço: {o.neck_cm} cm</p>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[220px]">{addr}</TableCell>
                      <TableCell className="font-semibold text-sm">{BRL(o.amount)}</TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={async (v) => { await api.put(`/admin/collar-orders/${o.collar_order_id}/status`, { status: v }); toast.success("Status atualizado e comprador avisado"); load(); }}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`collar-status-${o.collar_order_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="confeccao">Em confecção</SelectItem>
                            <SelectItem value="enviado">Enviado</SelectItem>
                            <SelectItem value="entregue">Entregue</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {collarOrders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum pedido de coleira ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="anilhas" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={anilhaDialog} onOpenChange={(o) => { setAnilhaDialog(o); if (!o) { setEditingAnilha(null); setAnilhaForm(EMPTY_ANILHA); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="add-anilha-btn"><Plus className="w-4 h-4 mr-1" /> Novo modelo</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingAnilha ? "Editar modelo" : "Novo modelo de anilha"}</DialogTitle></DialogHeader>
                <div className="space-y-3" data-testid="anilha-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nome</Label><Input value={anilhaForm.name} onChange={(e) => setAnilhaForm({ ...anilhaForm, name: e.target.value })} className="mt-1" data-testid="anilha-name-input" /></div>
                    <div>
                      <Label>Tier</Label>
                      <Select value={anilhaForm.tier} onValueChange={(v) => setAnilhaForm({ ...anilhaForm, tier: v })}>
                        <SelectTrigger className="mt-1" data-testid="anilha-tier-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="id">Anilha ID</SelectItem>
                          <SelectItem value="2d">Anilha 2D</SelectItem>
                          <SelectItem value="2d_pro">Anilha 2D Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Tagline</Label><Input value={anilhaForm.tagline} onChange={(e) => setAnilhaForm({ ...anilhaForm, tagline: e.target.value })} className="mt-1" data-testid="anilha-tagline-input" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Preço (R$)</Label><Input type="number" value={anilhaForm.price} onChange={(e) => setAnilhaForm({ ...anilhaForm, price: e.target.value })} className="mt-1" data-testid="anilha-price-input" /></div>
                    <div><Label>Custo (R$)</Label><Input type="number" value={anilhaForm.cost} onChange={(e) => setAnilhaForm({ ...anilhaForm, cost: e.target.value })} className="mt-1" data-testid="anilha-cost-input" /></div>
                    <div><Label>Estoque</Label><Input type="number" value={anilhaForm.stock} onChange={(e) => setAnilhaForm({ ...anilhaForm, stock: e.target.value })} className="mt-1" data-testid="anilha-stock-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Prazo produção (dias)</Label><Input type="number" value={anilhaForm.production_days} onChange={(e) => setAnilhaForm({ ...anilhaForm, production_days: e.target.value })} className="mt-1" data-testid="anilha-days-input" /></div>
                    <div>
                      <Label>Disponível</Label>
                      <Select value={String(anilhaForm.available)} onValueChange={(v) => setAnilhaForm({ ...anilhaForm, available: v === "true" })}>
                        <SelectTrigger className="mt-1" data-testid="anilha-available-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Sim</SelectItem>
                          <SelectItem value="false">Não</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Tamanhos (separados por vírgula)</Label><Input value={anilhaForm.sizes} onChange={(e) => setAnilhaForm({ ...anilhaForm, sizes: e.target.value })} placeholder="2,7 mm; 4,0 mm" className="mt-1" data-testid="anilha-sizes-input" /></div>
                  <div><Label>Cores (separadas por vírgula)</Label><Input value={anilhaForm.colors} onChange={(e) => setAnilhaForm({ ...anilhaForm, colors: e.target.value })} placeholder="Prata, Coral" className="mt-1" data-testid="anilha-colors-input" /></div>
                  <div><Label>Fotos — URLs (separadas por vírgula)</Label><Input value={anilhaForm.photos} onChange={(e) => setAnilhaForm({ ...anilhaForm, photos: e.target.value })} className="mt-1" data-testid="anilha-photos-input" /></div>
                  <div><Label>Características (separadas por vírgula)</Label><Input value={anilhaForm.features} onChange={(e) => setAnilhaForm({ ...anilhaForm, features: e.target.value })} className="mt-1" data-testid="anilha-features-input" /></div>
                  <div><Label>Descrição</Label><Input value={anilhaForm.description} onChange={(e) => setAnilhaForm({ ...anilhaForm, description: e.target.value })} className="mt-1" data-testid="anilha-desc-input" /></div>
                  <Button onClick={saveAnilha} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="anilha-save-btn">Salvar modelo</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-anilhas-table">
            <Table>
              <TableHeader><TableRow><TableHead>Modelo</TableHead><TableHead>Tier</TableHead><TableHead>Preço</TableHead><TableHead>Custo</TableHead><TableHead>Margem</TableHead><TableHead>Estoque</TableHead><TableHead>Prazo</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
              <TableBody>
                {anilhas.map((p) => (
                  <TableRow key={p.product_id} data-testid={`admin-anilha-${p.product_id}`}>
                    <TableCell className="font-medium text-sm">{p.name}</TableCell>
                    <TableCell className="text-xs">{p.tier}</TableCell>
                    <TableCell className="text-sm">{BRL(p.price)}</TableCell>
                    <TableCell className="text-sm">{BRL(p.cost)}</TableCell>
                    <TableCell className="text-sm font-semibold text-brand-mintDark">{BRL((p.price || 0) - (p.cost || 0))}</TableCell>
                    <TableCell className="text-sm">{p.stock}</TableCell>
                    <TableCell className="text-xs">{p.production_days}d</TableCell>
                    <TableCell className="text-xs">{p.available ? "Disponível" : "Indisponível"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEditAnilha(p)} data-testid={`edit-anilha-${p.product_id}`}><Plus className="w-4 h-4 rotate-45" /></Button>
                      <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/anilhas/${p.product_id}`); toast.success("Removido"); load(); }} data-testid={`delete-anilha-${p.product_id}`}><XCircle className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <h3 className="font-heading font-semibold text-lg mt-10 mb-4">Pedidos de anilhas</h3>
          <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="admin-anilha-orders-table">
            <Table>
              <TableHeader><TableRow><TableHead>Produto</TableHead><TableHead>Comprador</TableHead><TableHead>Detalhes</TableHead><TableHead>Endereço</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {anilhaOrders.map((o) => {
                  const a = o.delivery_address;
                  const addr = a ? `${a.rua || ""}, ${a.numero || ""}${a.complemento ? " - " + a.complemento : ""} — ${a.bairro || ""}, ${a.cidade || "Joinville"} — CEP ${a.cep || ""}` : "Não informado";
                  return (
                    <TableRow key={o.anilha_order_id} data-testid={`anilha-order-${o.anilha_order_id}`}>
                      <TableCell className="text-sm font-medium">{o.product_name}<p className="text-xs text-muted-foreground">{o.tier}</p></TableCell>
                      <TableCell className="text-xs"><p className="font-medium text-sm">{o.buyer_name}</p><p>{o.buyer_email}</p></TableCell>
                      <TableCell className="text-xs">
                        {o.pet_name && <p>Ave: <strong>{o.pet_name}</strong></p>}
                        <p>{o.size || "—"} • {o.color || "—"} • x{o.quantity}</p>
                        {o.engraving?.phone && <p>Tel: {o.engraving.phone}</p>}
                        {o.engraving?.qr_code && <p className="font-mono">Cód 2D: {o.engraving.qr_code}</p>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[220px]">{addr}</TableCell>
                      <TableCell className="font-semibold text-sm">{BRL(o.amount)}</TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={async (v) => { await api.put(`/admin/anilha-orders/${o.anilha_order_id}/status`, { status: v }); toast.success("Status atualizado e comprador avisado"); load(); }}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`anilha-status-${o.anilha_order_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="confeccao">Em confecção</SelectItem>
                            <SelectItem value="enviado">Enviado</SelectItem>
                            <SelectItem value="entregue">Entregue</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {anilhaOrders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum pedido de anilha ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="broadcast" className="mt-6">
          <div className="bg-white rounded-2xl border p-6 max-w-xl" data-testid="broadcast-form">
            <h3 className="font-heading font-semibold text-lg mb-1 flex items-center gap-2"><Megaphone className="w-5 h-5 text-brand-coral" /> Enviar notificação em massa</h3>
            <p className="text-xs text-muted-foreground mb-5">O envio via push (Expo) e email (Resend) será ativado na fase de integrações. Aqui a notificação fica registrada e visível no app.</p>
            <div className="space-y-3">
              <div><Label>Título</Label><Input value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} className="mt-1" data-testid="broadcast-title-input" /></div>
              <div><Label>Mensagem</Label><Input value={broadcast.message} onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })} className="mt-1" data-testid="broadcast-message-input" /></div>
              <div>
                <Label>Público</Label>
                <Select value={broadcast.audience} onValueChange={(v) => setBroadcast({ ...broadcast, audience: v })}>
                  <SelectTrigger className="mt-1" data-testid="broadcast-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="tutor">Tutores</SelectItem>
                    <SelectItem value="provider">Prestadores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={sendBroadcast} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="broadcast-send-btn">Registrar notificação</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
